import { useState, useMemo, useCallback } from "react";
import { Checkbox } from "@chakra-ui/react";
import { FiEdit2 } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { TableColumnFilter } from "newui/components/Filters";
import TextBoxHTML from "newui/components/TextBoxHTML";
import type {
	ReviewClarification,
	TriageStatus,
	DeclineReason,
} from "../../../../types/clarification-review.type";
import { TriageActions } from "./TriageActions";
import { TriageStatusBadge } from "./TriageStatusBadge";

type SortField = "supplier_name" | "reference_number" | "clarification_text" | "triage_status" | "created_by" | "created_at";
type SortDirection = "asc" | "desc";

interface ClarificationReviewTableProps {
	clarifications: ReviewClarification[];
	isLoading: boolean;
	error: Error | null;
	selectedIds: number[];
	onSelectionChange: (ids: number[]) => void;
	onTriage: (clarificationId: number, status: TriageStatus, declineReason?: string, declineCategory?: DeclineReason) => void;
	onMarkDuplicate: (clarificationId: number) => void;
	onApplyToAll?: (clarificationId: number) => void;
	onRowClick?: (clarification: ReviewClarification) => void;
	isTriaging: boolean;
	isReadOnly?: boolean;
}

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const sortClarifications = (
	items: ReviewClarification[],
	field: SortField,
	direction: SortDirection
): ReviewClarification[] => {
	return [...items].sort((a, b) => {
		let aVal: string | number | null = null;
		let bVal: string | number | null = null;

		switch (field) {
			case "supplier_name":
				aVal = a.supplier_name;
				bVal = b.supplier_name;
				break;
			case "reference_number":
				aVal = a.reference_number;
				bVal = b.reference_number;
				break;
			case "clarification_text":
				aVal = a.clarification_text;
				bVal = b.clarification_text;
				break;
			case "triage_status":
				aVal = a.triage_status ?? "";
				bVal = b.triage_status ?? "";
				break;
			case "created_by":
				aVal = a.created_by?.name ?? "";
				bVal = b.created_by?.name ?? "";
				break;
			case "created_at":
				aVal = a.created_at;
				bVal = b.created_at;
				break;
		}

		if (aVal === null || bVal === null) {
			return 0;
		}
		if (typeof aVal === "string" && typeof bVal === "string") {
			const cmp = aVal.localeCompare(bVal);
			return direction === "asc" ? cmp : -cmp;
		}
		if (typeof aVal === "number" && typeof bVal === "number") {
			return direction === "asc" ? aVal - bVal : bVal - aVal;
		}
		return 0;
	});
};

export const ClarificationReviewTable: React.FC<ClarificationReviewTableProps> = ({
	clarifications,
	isLoading,
	error,
	selectedIds,
	onSelectionChange,
	onTriage,
	onMarkDuplicate,
	onApplyToAll,
	onRowClick,
	isTriaging,
	isReadOnly = false,
}) => {
	const [supplierFilters, setSupplierFilters] = useState<string[]>([]);
	const [statusFilters, setStatusFilters] = useState<string[]>([]);
	const [sortField, setSortField] = useState<SortField>("supplier_name");
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

	const supplierOptions = useMemo(() => {
		const unique = [...new Set(clarifications.map((c) => c.supplier_name))];
		return unique.sort().map((name) => ({ value: name, label: name }));
	}, [clarifications]);

	const statusOptions = useMemo(() => [
		{ value: "pending", label: "Pending" },
		{ value: "accepted", label: "Accepted" },
		{ value: "declined", label: "Declined" },
		{ value: "duplicate", label: "Duplicate" },
	], []);

	const filteredItems = useMemo(() => {
		let items = clarifications;
		if (supplierFilters.length > 0) {
			items = items.filter((c) => supplierFilters.includes(c.supplier_name));
		}
		if (statusFilters.length > 0) {
			items = items.filter((c) => {
				const status = c.triage_status ?? "pending";
				return statusFilters.includes(status);
			});
		}
		return sortClarifications(items, sortField, sortDirection);
	}, [clarifications, supplierFilters, statusFilters, sortField, sortDirection]);

	const handleSort = useCallback((field: SortField) => {
		if (sortField === field) {
			setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	}, [sortField]);

	const allSelected = filteredItems.length > 0 && filteredItems.every((c) => selectedIds.includes(c.id));

	const handleSelectAll = useCallback(() => {
		if (allSelected) {
			onSelectionChange([]);
		} else {
			onSelectionChange(filteredItems.map((c) => c.id));
		}
	}, [allSelected, filteredItems, onSelectionChange]);

	const handleSelectRow = useCallback((id: number) => {
		if (selectedIds.includes(id)) {
			onSelectionChange(selectedIds.filter((s) => s !== id));
		} else {
			onSelectionChange([...selectedIds, id]);
		}
	}, [selectedIds, onSelectionChange]);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="review-table-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<p className="text-sm text-red-600" data-testid="review-table-error">
				Failed to load clarifications for review.
			</p>
		);
	}

	if (clarifications.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center"
				data-testid="review-table-empty"
			>
				<p className="text-sm text-gray-500">
					No clarifications were proposed during Initial Evaluation.
				</p>
				<p className="mt-1 text-xs text-gray-400">
					You can proceed to the next step.
				</p>
			</div>
		);
	}

	const thClass = "px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500";
	const sortIcon = (field: SortField) => {
		if (sortField !== field) {
			return null;
		}
		return <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>;
	};

	const sortableThClass = (field: SortField) =>
		cn(thClass, "cursor-pointer select-none hover:text-gray-700");

	return (
		<div className="w-full overflow-x-auto" data-testid="clarification-review-table">
			<table className="w-full min-w-[900px] border-collapse text-sm">
				<thead>
					<tr className="border-b border-gray-200 bg-gray-50">
						{!isReadOnly && (
							<th className="w-10 px-3 py-3">
								<Checkbox
									isChecked={allSelected}
									isIndeterminate={selectedIds.length > 0 && !allSelected}
									onChange={handleSelectAll}
									colorScheme="blue"
									aria-label="Select all clarifications"
									data-testid="select-all-checkbox"
								/>
							</th>
						)}
						<th className="text-left">
							<TableColumnFilter<string>
								label="Supplier"
								align="start"
								className={thClass}
								options={supplierOptions}
								value={supplierFilters}
								onChange={setSupplierFilters}
								isMulti
							/>
						</th>
						<th
							className={sortableThClass("reference_number")}
							onClick={() => handleSort("reference_number")}
						>
							Ref #{sortIcon("reference_number")}
						</th>
						<th
							className={sortableThClass("clarification_text")}
							onClick={() => handleSort("clarification_text")}
						>
							Question{sortIcon("clarification_text")}
						</th>
						<th className="text-left">
							<TableColumnFilter<string>
								label="Status"
								align="start"
								className={thClass}
								options={statusOptions}
								value={statusFilters}
								onChange={setStatusFilters}
								isMulti
							/>
						</th>
						<th
							className={sortableThClass("created_by")}
							onClick={() => handleSort("created_by")}
						>
							Created By{sortIcon("created_by")}
						</th>
						<th
							className={sortableThClass("created_at")}
							onClick={() => handleSort("created_at")}
						>
							Created{sortIcon("created_at")}
						</th>
						{!isReadOnly && <th className={thClass}>Actions</th>}
					</tr>
				</thead>
				<tbody>
					{filteredItems.map((clarification) => {
						const isSelected = selectedIds.includes(clarification.id);

						return (
							<tr
								key={clarification.id}
								className={cn(
									"border-b border-gray-100 transition-colors hover:bg-gray-50",
									isSelected && "bg-blue-50",
									onRowClick && "cursor-pointer"
								)}
								onClick={() => {
									if (onRowClick) {
										onRowClick(clarification);
									}
								}}
								data-testid={`review-row-${clarification.id}`}
							>
								{!isReadOnly && (
									<td className="px-3 py-3">
										<Checkbox
											isChecked={isSelected}
											onChange={(e) => {
												e.stopPropagation();
												handleSelectRow(clarification.id);
											}}
											onClick={(e) => e.stopPropagation()}
											colorScheme="blue"
											aria-label={`Select clarification ${clarification.reference_number}`}
										/>
									</td>
								)}
								<td className="px-3 py-3 text-sm font-medium text-gray-700 max-w-[200px]">
									{clarification.supplier_name}
								</td>
								<td className="px-3 py-3 text-sm text-gray-500 font-mono">
									#{clarification.reference_number}
								</td>
								<td className="px-3 py-3 text-sm text-gray-600 max-w-[350px]">
									<TextBoxHTML className="line-clamp-2" html={clarification.clarification_text} />
									<p className="mt-0.5 text-xs text-gray-400">
										Relates to: {clarification.relates_to || "—"}
									</p>
									{clarification.source_clarification_id && (
										<p className="mt-0.5 text-xs text-blue-500">
											Duplicated from #{clarification.source_clarification_id}
										</p>
									)}
									{clarification.original_clarification_text !== null &&
										clarification.original_clarification_text !== clarification.clarification_text && (
										<span
											className="mt-0.5 inline-flex items-center gap-0.5 text-xs text-amber-600"
											data-testid={`edited-badge-${clarification.id}`}
											title={`Edited${clarification.edited_by_name ? ` by ${clarification.edited_by_name}` : ""}${clarification.edited_at ? ` on ${new Date(clarification.edited_at).toLocaleDateString()}` : ""}`}
										>
											<FiEdit2 size={10} />
											Edited
										</span>
									)}
								</td>
								<td className="px-3 py-3">
									<TriageStatusBadge status={clarification.triage_status} />
									{clarification.decline_reason && (
										<p className="mt-0.5 text-xs text-gray-400 max-w-[150px] truncate" title={clarification.decline_reason}>
											{clarification.decline_reason}
										</p>
									)}
									{clarification.duplicate_of_id && (
										<p className="mt-0.5 text-xs text-gray-400">
											Duplicate of #{clarification.duplicate_of_id}
										</p>
									)}
								</td>
								<td className="px-3 py-3 text-sm text-gray-600">
									<div>
										{clarification.created_by?.name ?? "—"}
									</div>
									{clarification.created_by?.company_name && (
										<p className="text-xs text-gray-400">
											{clarification.created_by.company_name}
										</p>
									)}
								</td>
								<td className="px-3 py-3 text-xs text-gray-500">
									{formatDate(clarification.created_at)}
								</td>
								{!isReadOnly && (
									<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
										<TriageActions
											clarification={clarification}
											onTriage={onTriage}
											onMarkDuplicate={onMarkDuplicate}
											onApplyToAll={onApplyToAll}
											isTriaging={isTriaging}
										/>
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>
			{filteredItems.length === 0 && clarifications.length > 0 && (
				<div className="py-6 text-center text-sm text-gray-400">
					No clarifications match the selected filters.
				</div>
			)}
		</div>
	);
};
